import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CatButton } from "@cat-digital-blocks/react/bedrock/components/Button";
import { CatHeading } from "@cat-digital-blocks/react/bedrock/components/Heading";
import { CatTabs } from "@cat-digital-blocks/react/bedrock/components/Tabs";
import { CatTab } from "@cat-digital-blocks/react/bedrock/components/Tab";
import { CatTabPanel } from "@cat-digital-blocks/react/bedrock/components/TabPanel";
import { CatTable } from "@cat-digital-blocks/react/bedrock/components/Table";
import { CatTableObject } from "@cat-digital-blocks/react/bedrock/components/TableObject";
import { CatTableBody } from "@cat-digital-blocks/react/bedrock/components/TableBody";
import { CatTableCell } from "@cat-digital-blocks/react/bedrock/components/TableCell";
import { CatTableHeader } from "@cat-digital-blocks/react/bedrock/components/TableHeader";
import { CatTableHeaderCell } from "@cat-digital-blocks/react/bedrock/components/TableHeaderCell";
import { CatTableRow } from "@cat-digital-blocks/react/bedrock/components/TableRow";
import { CatInlineNotification } from "@cat-digital-blocks/react/bedrock/components/InlineNotification";
import { CatIconClose } from "@cat-digital-blocks/react/bedrock/components/Icons/Close";
import { CatIconEdit } from "@cat-digital-blocks/react/bedrock/components/Icons/Edit";
import {
  useGetRuleMappingsQuery,
  useDeleteRuleMappingMutation,
} from "@services/dcmApi";
import type { RuleMappingResponse } from "@/types/api";
import styles from "./RuleRegistration.module.scss";
import CreateRuleModal from "./CreateRuleModal";
import EditRuleModal from "./EditRuleModal";

// PAL Types for nested tabs
const PAL_TYPES = ["LEADS", "OPPORTUNITY", "TASKS", "CASE"] as const;
type PalType = (typeof PAL_TYPES)[number];

interface RuleRegistrationProps {
  currentUserName?: string;
}

const RuleRegistration = ({ currentUserName }: RuleRegistrationProps) => {
  const { t } = useTranslation();

  const [selectedPalTypeIndex, setSelectedPalTypeIndex] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [modalPalType, setModalPalType] = useState<PalType>("LEADS");
  const [editingRule, setEditingRule] = useState<RuleMappingResponse | null>(
    null,
  );

  // Store the intended tab index to prevent unwanted changes
  const intendedTabIndex = useRef(selectedPalTypeIndex);

  // Update the ref whenever we intentionally change the tab
  useEffect(() => {
    intendedTabIndex.current = selectedPalTypeIndex;
  }, [selectedPalTypeIndex]);

  const [notification, setNotification] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const selectedPalType: PalType = PAL_TYPES[selectedPalTypeIndex];

  // Fetch rule mappings for the selected PAL type
  const {
    data: ruleMappings = [],
    isLoading,
    error,
    refetch: refetchMappings,
  } = useGetRuleMappingsQuery(
    { palType: selectedPalType },
    {
      refetchOnMountOrArgChange: true,
      pollingInterval: 0,
    },
  );

  const [deleteRule, { isLoading: isDeleting }] =
    useDeleteRuleMappingMutation();

  const handlePalTypeTabChange = useCallback(
    (event: CustomEvent) => {
      // Stop propagation to prevent bubbling to parent tabs
      event.stopPropagation();

      const newIndex = event.detail?.activeTabIndex;

      if (typeof newIndex !== "number") {
        return;
      }

      // Ignore tab changes when modal is open
      if (isCreateModalOpen || editingRule) {
        return;
      }

      if (newIndex >= 0 && newIndex < PAL_TYPES.length) {
        intendedTabIndex.current = newIndex;
        setSelectedPalTypeIndex(newIndex);
      }
    },
    [isCreateModalOpen, editingRule],
  );

  // Handle Add Rule button click
  const handleAddRuleClick = useCallback(
    (palType: PalType) => (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();

      const palTypeIndex = PAL_TYPES.indexOf(palType);
      if (palTypeIndex !== -1) {
        setSelectedPalTypeIndex(palTypeIndex);
      }

      setModalPalType(palType);
      setIsCreateModalOpen(true);
    },
    [],
  );

  const handleDeleteRule = useCallback(
    async (rule: RuleMappingResponse) => {
      if (
        !window.confirm(
          t("admin.ruleRegistration.confirmDelete", {
            rule: rule.ruleDescription || rule.attributeName,
          }),
        )
      ) {
        return;
      }
      try {
        await deleteRule({
          ruleId: rule.id,
        }).unwrap();
        setNotification({
          type: "success",
          message: t("admin.ruleRegistration.deleteSuccess"),
        });
        refetchMappings();
        setTimeout(() => setNotification(null), 5000);
      } catch {
        setNotification({
          type: "error",
          message: t("admin.ruleRegistration.deleteError"),
        });
      }
    },
    [deleteRule, refetchMappings, t],
  );

  const handleCreateSuccess = useCallback(() => {
    setIsCreateModalOpen(false);
    setNotification({
      type: "success",
      message: t("admin.ruleRegistration.createSuccess"),
    });
    refetchMappings();
    setTimeout(() => setNotification(null), 5000);
  }, [refetchMappings, t]);

  const handleEditSuccess = useCallback(() => {
    setEditingRule(null);
    setNotification({
      type: "success",
      message: t("admin.ruleRegistration.updateSuccess"),
    });
    refetchMappings();
    setTimeout(() => setNotification(null), 5000);
  }, [refetchMappings, t]);

  // Sort rule mappings by attribute name
  const sortedMappings = useMemo(() => {
    return [...ruleMappings].sort((a, b) =>
      (a.attributeName || "").localeCompare(b.attributeName || ""),
    );
  }, [ruleMappings]);

  // Format date for display
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const renderTable = () => {
    if (error) {
      return (
        <CatInlineNotification variant="error">
          {t("admin.ruleRegistration.error.loadFailed")}
        </CatInlineNotification>
      );
    }

    return (
      <CatTableObject minWidth="100%">
        <CatTable
          className={styles.ruleTable}
          key={`table-${selectedPalType}`}
          arialabel={t("admin.ruleRegistration.table.ariaLabel")}
        >
          <CatTableHeader isFrozen>
            <CatTableRow isLoading={isLoading}>
              <CatTableHeaderCell width="13%" isFrozen>
                {t("admin.ruleRegistration.table.dataSource")}
              </CatTableHeaderCell>
              <CatTableHeaderCell width="13%" isFrozen>
                {t("admin.ruleRegistration.table.attribute")}
              </CatTableHeaderCell>
              <CatTableHeaderCell width="18%" isFrozen>
                {t("admin.ruleRegistration.table.ruleDescription")}
              </CatTableHeaderCell>
              <CatTableHeaderCell width="12%" isFrozen>
                {t("admin.ruleRegistration.table.defaultValue")}
              </CatTableHeaderCell>
              <CatTableHeaderCell width="10%" isFrozen>
                {t("admin.ruleRegistration.table.ruleOperator")}
              </CatTableHeaderCell>
              <CatTableHeaderCell width="12%" isFrozen>
                {t("admin.ruleRegistration.table.validFrom")}
              </CatTableHeaderCell>
              <CatTableHeaderCell width="12%" isFrozen>
                {t("admin.ruleRegistration.table.validUntil")}
              </CatTableHeaderCell>
              <CatTableHeaderCell width="10%" isFrozen>
                {t("admin.ruleRegistration.table.actions")}
              </CatTableHeaderCell>
            </CatTableRow>
          </CatTableHeader>
          <CatTableBody>
            {isLoading ? (
              // Show loading skeleton rows
              [...Array(3)].map((_, index) => (
                <CatTableRow key={`loading-${index}`} isLoading>
                  <CatTableCell>-</CatTableCell>
                  <CatTableCell>-</CatTableCell>
                  <CatTableCell>-</CatTableCell>
                  <CatTableCell>-</CatTableCell>
                  <CatTableCell>-</CatTableCell>
                  <CatTableCell>-</CatTableCell>
                  <CatTableCell>-</CatTableCell>
                  <CatTableCell>-</CatTableCell>
                </CatTableRow>
              ))
            ) : sortedMappings.length === 0 ? (
              <CatTableRow>
                <CatTableCell colspan={8} className={styles.emptyRow}>
                  {t("admin.ruleRegistration.noRules")}
                </CatTableCell>
              </CatTableRow>
            ) : (
              sortedMappings.map((rule) => (
                <CatTableRow key={rule.id}>
                  <CatTableCell>{rule.sourceName || "-"}</CatTableCell>
                  <CatTableCell>{rule.attributeName || "-"}</CatTableCell>
                  <CatTableCell>{rule.ruleDescription || "-"}</CatTableCell>
                  <CatTableCell>{rule.defaultValue || "-"}</CatTableCell>
                  <CatTableCell>{rule.ruleOperator || "-"}</CatTableCell>
                  <CatTableCell>{formatDate(rule.validFrom)}</CatTableCell>
                  <CatTableCell>{formatDate(rule.validUntil)}</CatTableCell>
                  <CatTableCell>
                    <div className={styles.actionButtons}>
                      <CatButton
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingRule(rule)}
                        title={t("common.edit")}
                      >
                        <CatIconEdit size="sm" />
                      </CatButton>
                      <CatButton
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRule(rule)}
                        disabled={isDeleting}
                        title={t("common.delete")}
                      >
                        <CatIconClose size="sm" />
                      </CatButton>
                    </div>
                  </CatTableCell>
                </CatTableRow>
              ))
            )}
          </CatTableBody>
        </CatTable>
      </CatTableObject>
    );
  };

  const renderPalTypeContent = (palType: PalType) => (
    <div className={styles.tabContent}>
      <div className={styles.tabHeader}>
        <span className={styles.palTypeLabel}>
          {t("admin.ruleRegistration.rulesFor", { palType })}
        </span>
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <CatButton variant="primary" onClick={handleAddRuleClick(palType)}>
            {t("admin.ruleRegistration.createRule")}
          </CatButton>
        </div>
      </div>
      {renderTable()}
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <CatHeading variant="title-sm">
          {t("admin.ruleRegistration.title")}
        </CatHeading>
      </div>

      {notification && (
        <CatInlineNotification
          variant={notification.type === "success" ? "success" : "error"}
          className={styles.notification}
        >
          {notification.message}
        </CatInlineNotification>
      )}

      {/* Nested PAL Type Tabs */}
      <div className={styles.palTypeTabs}>
        <CatTabs
          id="rule-pal-type-tabs"
          activeIndex={selectedPalTypeIndex}
          onBlTabChange={handlePalTypeTabChange}
        >
          <CatTab>{t("palTypes.leads")}</CatTab>
          <CatTabPanel slot="panel">
            {renderPalTypeContent("LEADS")}
          </CatTabPanel>

          <CatTab>{t("palTypes.opportunity")}</CatTab>
          <CatTabPanel slot="panel">
            {renderPalTypeContent("OPPORTUNITY")}
          </CatTabPanel>

          <CatTab>{t("palTypes.tasks")}</CatTab>
          <CatTabPanel slot="panel">
            {renderPalTypeContent("TASKS")}
          </CatTabPanel>

          <CatTab>{t("palTypes.case")}</CatTab>
          <CatTabPanel slot="panel">{renderPalTypeContent("CASE")}</CatTabPanel>
        </CatTabs>
      </div>

      {/* Create Rule Modal */}
      {isCreateModalOpen && (
        <CreateRuleModal
          key={`create-${modalPalType}`}
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
          palType={modalPalType}
          userName={currentUserName}
        />
      )}

      {/* Edit Rule Modal */}
      {editingRule && (
        <EditRuleModal
          isOpen={!!editingRule}
          onClose={() => setEditingRule(null)}
          onSuccess={handleEditSuccess}
          rule={editingRule}
          userName={currentUserName}
        />
      )}
    </div>
  );
};

export default RuleRegistration;
